/**
 * Pure analysis helpers for the in-thread table block.
 *
 * These read the hast node ONLY to decide presentation (which columns are
 * numeric, which cells are statuses) and to build copy/download payloads. The
 * cells themselves are still rendered by ReactMarkdown, so links, emphasis,
 * code and citation markers inside a cell survive — flattening the node to
 * text for rendering silently dropped them.
 *
 * Kept separate from the component so the parsing is directly unit-testable
 * without a DOM.
 */

import type { Element, Nodes } from 'hast';
import { isAllowedLinkHref } from '@bower/shared/utils/linkHref';

/** Status vocabulary → tone. Sage / yellow / neutral. */
const STATUS_TONES: Record<string, 'success' | 'warning' | 'neutral'> = {
  complete: 'success',
  completed: 'success',
  done: 'success',
  pass: 'success',
  passed: 'success',
  ok: 'success',
  'in range': 'success',
  active: 'success',
  'in progress': 'warning',
  pending: 'warning',
  running: 'warning',
  review: 'warning',
  flagged: 'warning',
  'out of range': 'warning',
  blocked: 'neutral',
  'n/a': 'neutral',
  skipped: 'neutral',
  'not started': 'neutral',
  archived: 'neutral',
};

export type StatusTone = 'success' | 'warning' | 'neutral';

/** The tone for a cell's text, or null when it isn't a known status word. */
export function statusToneFor(value: string): StatusTone | null {
  return STATUS_TONES[value.trim().toLowerCase()] ?? null;
}

const UNIT_SUFFIX_RE = /(%|°C|°F|mm|cm|mL|mg|kg|µM|mM|nM|[LgM])$/;

/**
 * Numbers, tolerating thousands separators and a trailing unit. Strip, then
 * let `Number` decide — a single regex covering sign, digits, separators and
 * decimals is both harder to read and ambiguous enough to trip the ReDoS lint.
 */
export function isNumericValue(value: string): boolean {
  const stripped = value.trim().replace(UNIT_SUFFIX_RE, '').trim().replace(/,/g, '');
  return stripped.length > 0 && Number.isFinite(Number(stripped));
}

/** Flatten a hast subtree to its visible text. */
export function textOf(node: Nodes): string {
  if (node.type === 'text') return node.value;
  if ('children' in node) return node.children.map(textOf).join('');
  return '';
}

function childElements(node: Element | undefined, tagName: string): Element[] {
  if (!node) return [];
  return node.children.filter(
    (child): child is Element => child.type === 'element' && child.tagName === tagName,
  );
}

export interface TableData {
  readonly headers: readonly string[];
  readonly rows: readonly (readonly string[])[];
  /** 0-based indices of columns whose every non-empty cell parses as a number. */
  readonly numericColumns: ReadonlySet<number>;
}

export function parseTable(node: Element): TableData {
  const thead = childElements(node, 'thead')[0];
  const tbody = childElements(node, 'tbody')[0];

  const headerRow = thead ? childElements(thead, 'tr')[0] : undefined;
  const headers = headerRow
    ? [...childElements(headerRow, 'th'), ...childElements(headerRow, 'td')].map((cell) =>
        textOf(cell).trim(),
      )
    : [];

  const rows = childElements(tbody, 'tr').map((tr) =>
    [...childElements(tr, 'td'), ...childElements(tr, 'th')].map((cell) => textOf(cell).trim()),
  );

  const numericColumns = new Set<number>();
  for (let col = 0; col < headers.length; col += 1) {
    const values = rows.map((row) => row[col] ?? '').filter((v) => v.length > 0);
    if (values.length > 0 && values.every(isNumericValue)) numericColumns.add(col);
  }

  return { headers, rows, numericColumns };
}

const FORMULA_LEAD = /^[=+\-@\t\r]/;

/**
 * Leading `=`, `+`, `-`, `@`, tab or CR make a spreadsheet treat the cell as a
 * formula (CWE-1236). Table content is model output, often derived from
 * imported documents, so it is not trustworthy input — prefix it with an
 * apostrophe so the cell stays literal. Quoting alone does NOT neutralise it.
 */
function neutraliseFormula(cell: string): string {
  return FORMULA_LEAD.test(cell) ? `'${cell}` : cell;
}

/**
 * Tab-separated so it pastes straight into a spreadsheet — which is exactly
 * why it needs the same formula guard as the CSV: Excel and Sheets parse a
 * tab-delimited clipboard payload into cells and evaluate a leading `=` there
 * too. In-cell tabs/newlines are flattened, or they forge extra columns.
 */
export function toTsv({ headers, rows }: TableData): string {
  return [headers, ...rows]
    .map((row) => row.map((c) => neutraliseFormula(c).replace(/[\t\r\n]/g, ' ')).join('\t'))
    .join('\n');
}

function escapeCsvCell(cell: string): string {
  // Normalise CR/CRLF first: the quote trigger below only looks for \n, so a
  // bare CR would otherwise ship unquoted and split the record in Excel.
  const safe = neutraliseFormula(cell.replace(/\r\n?/g, '\n'));
  return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function toCsv({ headers, rows }: TableData): string {
  return [headers, ...rows].map((row) => row.map(escapeCsvCell).join(',')).join('\n');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * The href a copied link should carry once it leaves Bower, or null to keep
 * only its text. An in-app path (`/notes/<id>`, how Bird cites workspace
 * entities) becomes absolute on this origin, since a relative href pasted into
 * Docs or Sheets points nowhere. Otherwise only what the editor itself would
 * store: absolute http(s) or mailto.
 */
function exportHref(href: unknown, origin: string): string | null {
  if (typeof href !== 'string') return null;
  if (href.startsWith('/') && !href.startsWith('//')) return `${origin}${href}`;
  return isAllowedLinkHref(href) ? href : null;
}

/** A cell's content as HTML: its text, escaped, with links kept and other markup dropped. */
function cellHtml(node: Nodes, origin: string): string {
  if (node.type === 'text') return escapeHtml(node.value);
  if (!('children' in node)) return '';
  const inner = node.children.map((child) => cellHtml(child, origin)).join('');
  if (node.type === 'element' && node.tagName === 'a') {
    const href = exportHref(node.properties?.href, origin);
    return href ? `<a href="${escapeHtml(href)}">${inner}</a>` : inner;
  }
  return inner;
}

/**
 * An HTML `<table>` for the clipboard's `text/html` flavour, read straight from
 * the hast node because `parseTable` has already flattened the links away.
 * Rich-text targets (Docs, Word, Notion, email) paste this as a real table
 * with working links, where the TSV arrived as a run of tab-separated text;
 * Sheets and Excel prefer it too, so they keep the links as well.
 *
 * The same formula guard as the TSV, for the same reason: a spreadsheet reads
 * a leading `=` in a pasted HTML cell as a formula. The cost is that a
 * negative number pastes into Docs or Word as `'-0.5`, exactly as the TSV
 * already did; accepted, because the clipboard cannot know whether the paste
 * target evaluates formulas, and the one that does is where the harm is.
 */
export function toHtml(node: Element, origin: string): string {
  const thead = childElements(node, 'thead')[0];
  const tbody = childElements(node, 'tbody')[0];
  const rowHtml = (tr: Element, tag: 'th' | 'td') =>
    `<tr>${[...childElements(tr, 'th'), ...childElements(tr, 'td')]
      .map((cell) => {
        const guard = FORMULA_LEAD.test(textOf(cell).trim()) ? "'" : '';
        return `<${tag}>${guard}${cellHtml(cell, origin).trim()}</${tag}>`;
      })
      .join('')}</tr>`;
  const header = thead
    ? childElements(thead, 'tr')
        .map((tr) => rowHtml(tr, 'th'))
        .join('')
    : '';
  const body = childElements(tbody, 'tr')
    .map((tr) => rowHtml(tr, 'td'))
    .join('');
  return `<table><thead>${header}</thead><tbody>${body}</tbody></table>`;
}

/** "4 rows · 6 columns" */
export function summarise({ headers, rows }: TableData): string {
  const rowLabel = rows.length === 1 ? 'row' : 'rows';
  const colLabel = headers.length === 1 ? 'column' : 'columns';
  return `${rows.length} ${rowLabel} · ${headers.length} ${colLabel}`;
}
